import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Spinner } from '../ui';
import { cn } from '../../shared/lib/cn';
import type {
  SemanticContextSetupReadinessResponse,
  SemanticContextSetupStage,
  SemanticContextSetupStageState,
} from '../../hooks/useWorkspaceSemanticContext';
import { WorkspaceWarehouseProfilingPanel } from './WorkspaceWarehouseProfilingPanel';

type SetupActionTarget = 'scope' | 'catalog' | 'ontology' | 'context_packs' | 'mcp' | 'smoke';

interface WorkspaceSemanticSetupActions {
  readiness?: SemanticContextSetupReadinessResponse | null;
  isLoading?: boolean;
  isError?: boolean;
  onOpenWarehouseConnections: () => void;
  onOpenWarehouseScope: () => void;
  onOpenCatalogCoverage: () => void;
  onOpenOntology: () => void;
  onOpenContextPacks: () => void;
  onOpenMcp?: () => void;
  onRunSmoke?: () => Promise<unknown>;
  isSmokePending?: boolean;
  isSmokeError?: boolean;
  profilingConnectorId?: number | null;
}

export interface WorkspaceSemanticSetupGuideProps extends WorkspaceSemanticSetupActions {}

export interface WorkspaceSemanticSetupSummaryCardProps extends WorkspaceSemanticSetupActions {
  onOpenSetupGuide: () => void;
}

const STAGE_ACTION_TARGETS: Record<string, SetupActionTarget> = {
  connector_configured: 'scope',
  connector_health: 'scope',
  oauth_app_configuration: 'scope',
  setup_grant_state: 'scope',
  metadata_sync: 'scope',
  object_inventory: 'scope',
  workspace_scope: 'scope',
  profiling_coverage: 'catalog',
  profiling_failures: 'catalog',
  catalog_descriptions: 'catalog',
  generation_runs: 'ontology',
  generation_recovery: 'ontology',
  pending_review: 'ontology',
  candidate_pack_validation: 'context_packs',
  active_pack: 'context_packs',
  index_health: 'context_packs',
  structured_tool_exposure: 'mcp',
  bigquery_dry_run: 'smoke',
  bounded_sql_execution: 'smoke',
};

function stateBadge(state: SemanticContextSetupStageState) {
  if (state === 'complete') return { variant: 'success' as const, label: 'Complete' };
  if (state === 'blocked') return { variant: 'danger' as const, label: 'Blocked' };
  if (state === 'warning') return { variant: 'warning' as const, label: 'Warning' };
  if (state === 'in_progress') return { variant: 'info' as const, label: 'In progress' };
  return { variant: 'secondary' as const, label: 'Not started' };
}

function stateIcon(state: SemanticContextSetupStageState) {
  const className = 'h-5 w-5';
  if (state === 'complete') return <CheckCircleIcon className={cn(className, 'text-emerald-700 dark:text-emerald-300')} />;
  if (state === 'blocked') return <XCircleIcon className={cn(className, 'text-rose-700 dark:text-rose-300')} />;
  if (state === 'warning') return <ExclamationTriangleIcon className={cn(className, 'text-amber-700 dark:text-amber-300')} />;
  if (state === 'in_progress') return <ArrowPathIcon className={cn(className, 'text-blue-700 dark:text-blue-300')} />;
  return <ClockIcon className={cn(className, 'text-gray-500 dark:text-gray-400')} />;
}

function actionLabel(stage: SemanticContextSetupStage): string | null {
  if (stage.next_action) return stage.next_action;
  if (stage.state === 'complete') {
    if (stage.key === 'ontology_draft' || stage.key === 'ontology_review') return 'Open ontology';
    if (stage.key === 'active_pack' || stage.key === 'runtime_pack_compiled') return 'Open context packs';
    if (stage.key === 'mcp_readiness') return 'Open MCP';
    if (stage.key === 'catalog_descriptions') return 'Review coverage';
    if (stage.key === 'table_scope') return 'Review scope';
  }
  return null;
}

function statusMeta(status: SemanticContextSetupReadinessResponse['overall_status']) {
  if (status === 'ready') return { variant: 'success' as const, label: 'Ready' };
  if (status === 'blocked') return { variant: 'danger' as const, label: 'Blocked' };
  if (status === 'warning') return { variant: 'warning' as const, label: 'Warnings' };
  return { variant: 'info' as const, label: 'In progress' };
}

function targetForStage(stage: SemanticContextSetupStage): SetupActionTarget | null {
  return STAGE_ACTION_TARGETS[stage.key] ?? null;
}

function openSetupTarget(
  target: SetupActionTarget | null,
  readiness: SemanticContextSetupReadinessResponse,
  actions: Pick<
    WorkspaceSemanticSetupActions,
    | 'onOpenWarehouseConnections'
    | 'onOpenWarehouseScope'
    | 'onOpenCatalogCoverage'
    | 'onOpenOntology'
    | 'onOpenContextPacks'
    | 'onOpenMcp'
    | 'onRunSmoke'
  >,
) {
  if (target === 'scope') {
    if (!readiness.structured_scope_summary.connector_configured) {
      actions.onOpenWarehouseConnections();
      return;
    }
    actions.onOpenWarehouseScope();
    return;
  }
  if (target === 'catalog') actions.onOpenCatalogCoverage();
  if (target === 'ontology') actions.onOpenOntology();
  if (target === 'context_packs') actions.onOpenContextPacks();
  if (target === 'mcp') actions.onOpenMcp?.();
  if (target === 'smoke') void actions.onRunSmoke?.().catch(() => undefined);
}

function firstSetupIssue(readiness: SemanticContextSetupReadinessResponse): SemanticContextSetupStage | null {
  if (readiness.first_blocker) {
    return readiness.stages.find((stage) => stage.key === readiness.first_blocker?.stage_key) ?? null;
  }
  return (
    readiness.stages.find((stage) => stage.state === 'blocked') ??
    readiness.stages.find((stage) => stage.state === 'warning') ??
    readiness.stages.find((stage) => stage.state === 'in_progress') ??
    readiness.stages.find((stage) => stage.state === 'not_started') ??
    null
  );
}

export function WorkspaceSemanticSetupSummaryCard({
  readiness,
  isLoading = false,
  isError = false,
  onOpenSetupGuide,
  onOpenWarehouseConnections,
  onOpenWarehouseScope,
  onOpenCatalogCoverage,
  onOpenOntology,
  onOpenContextPacks,
  onOpenMcp,
}: WorkspaceSemanticSetupSummaryCardProps) {
  if (isLoading) {
    return (
      <section className="rounded-[16px] border border-gray-200 bg-white px-5 py-4 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Spinner size="sm" />
          Loading setup health...
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="rounded-[16px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
        Setup health is unavailable. Semantic context controls remain available below.
      </section>
    );
  }

  if (!readiness || readiness.overall_status === 'unsupported') {
    return null;
  }

  const meta = statusMeta(readiness.overall_status);
  const completedCount = readiness.stages.filter((stage) => stage.state === 'complete').length;
  const issue = firstSetupIssue(readiness);
  const issueBadge = issue ? stateBadge(issue.state) : null;
  const issueAction = issue ? actionLabel(issue) : null;
  const issueTarget = issue ? targetForStage(issue) : null;
  const activePackVersion = readiness.runtime_pack_summary.active_pack?.version;
  const mcpReady = readiness.mcp_summary?.status === 'ready';
  const actions = {
    onOpenWarehouseConnections,
    onOpenWarehouseScope,
    onOpenCatalogCoverage,
    onOpenOntology,
    onOpenContextPacks,
    onOpenMcp,
  };

  return (
    <section className="rounded-[16px] border border-gray-200 bg-white px-5 py-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
              Setup health
            </p>
            <Badge variant={meta.variant} className="px-2 py-0.5">
              {meta.label}
            </Badge>
          </div>
          <h2 className="mt-1 font-subtitle text-xl leading-tight text-charcoal dark:text-gray-100">
            {completedCount} of {readiness.stages.length} setup checks complete
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
            {issue
              ? `${issue.label}: ${issue.reason}`
              : 'Structured runtime, ontology, Runtime version, and MCP readiness checks are complete.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="secondary" className="px-2 py-0.5">
              {activePackVersion ? `Version v${activePackVersion} live` : 'No live version'}
            </Badge>
            <Badge variant={mcpReady ? 'success' : 'secondary'} className="px-2 py-0.5">
              MCP {mcpReady ? 'ready' : 'not ready'}
            </Badge>
            {issueBadge && (
              <Badge variant={issueBadge.variant} className="px-2 py-0.5">
                {issueBadge.label}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
          {issueAction && issueTarget && !(issueTarget === 'mcp' && !onOpenMcp) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openSetupTarget(issueTarget, readiness, actions)}
            >
              {issueAction}
              <ArrowRightIcon className="h-4 w-4" />
            </Button>
          )}
          <Button type="button" size="sm" onClick={onOpenSetupGuide}>
            Open setup guide
            <ArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

export function WorkspaceSemanticSetupGuide({
  readiness,
  isLoading = false,
  isError = false,
  onOpenWarehouseConnections,
  onOpenWarehouseScope,
  onOpenCatalogCoverage,
  onOpenOntology,
  onOpenContextPacks,
  onOpenMcp,
  onRunSmoke,
  isSmokePending = false,
  isSmokeError = false,
  profilingConnectorId,
}: WorkspaceSemanticSetupGuideProps) {
  if (isLoading) {
    return (
      <section className="rounded-[16px] border border-gray-200 bg-white px-5 py-4 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <Spinner size="sm" />
          Loading guided setup readiness...
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="rounded-[16px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
        Guided setup readiness is unavailable. Existing semantic context controls remain available below.
      </section>
    );
  }

  if (!readiness || readiness.overall_status === 'unsupported') {
    return null;
  }

  const firstBlocker = readiness.first_blocker;
  const status = statusMeta(readiness.overall_status);
  const actions = {
    onOpenWarehouseConnections,
    onOpenWarehouseScope,
    onOpenCatalogCoverage,
    onOpenOntology,
    onOpenContextPacks,
    onOpenMcp,
    onRunSmoke,
  };

  return (
    <section className="rounded-[16px] border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
            Guided structured setup
          </p>
          <h2 className="mt-1 font-title text-[26px] leading-tight text-charcoal dark:text-gray-100">
            Structured data readiness
          </h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            Follow one persisted path from warehouse authorization and profiling through ontology generation,
            pack indexing, tool exposure, and bounded SQL verification.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant} className="w-fit px-3 py-1">
            {status.label}
          </Badge>
          {onRunSmoke && (
            <Button
              type="button"
              size="sm"
              onClick={() => void onRunSmoke().catch(() => undefined)}
              disabled={isSmokePending}
            >
              {isSmokePending && <Spinner size="sm" />}
              {isSmokePending ? 'Running smoke…' : 'Run readiness smoke'}
            </Button>
          )}
        </div>
      </div>

      {readiness.smoke_summary?.executed && (
        <div
          className="border-b border-gray-200 bg-gray-50 px-5 py-3 text-sm text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300"
          role="status"
          aria-live="polite"
        >
          Bounded smoke: dry run {readiness.smoke_summary.dry_run_status}; SQL{' '}
          {readiness.smoke_summary.sql_execution_status}. Limits:{' '}
          {readiness.smoke_summary.row_limit ?? 0} row,{' '}
          {readiness.smoke_summary.bytes_limit ?? 0} bytes billed.
        </div>
      )}

      {isSmokeError && (
        <div
          className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200"
          role="alert"
        >
          The bounded readiness smoke could not complete. Review the first blocker, then retry.
        </div>
      )}

      {profilingConnectorId !== undefined && (
        <WorkspaceWarehouseProfilingPanel
          workspaceId={readiness.workspace_id}
          connectorId={profilingConnectorId}
        />
      )}

      {firstBlocker && (
        <div className="border-b border-gray-200 bg-rose-50 px-5 py-4 text-sm text-rose-900 dark:border-dark-border dark:bg-rose-900/10 dark:text-rose-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-semibold">First blocker: {firstBlocker.message}</p>
                <p className="mt-1 text-rose-800/80 dark:text-rose-100/80">{firstBlocker.next_action}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-current"
              onClick={() => openSetupTarget(STAGE_ACTION_TARGETS[firstBlocker.stage_key] ?? null, readiness, actions)}
            >
              Go to stage
              <ArrowRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ol className="divide-y divide-gray-200 dark:divide-dark-border">
        {readiness.stages.map((stage, index) => {
          const badge = stateBadge(stage.state);
          const target = targetForStage(stage);
          const label = actionLabel(stage);
          const actionDisabled = target === 'mcp' && !onOpenMcp;
          return (
            <li key={stage.key} className="grid gap-3 px-5 py-4 md:grid-cols-[32px_1fr_auto] md:items-center">
              <div className="flex items-center gap-2 md:block">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="md:hidden">{stateIcon(stage.state)}</span>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="hidden md:inline-flex">{stateIcon(stage.state)}</span>
                  <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{stage.label}</h3>
                  <Badge variant={badge.variant} className="px-2 py-0.5">
                    {badge.label}
                  </Badge>
                  {stage.blocks_runtime && stage.state === 'blocked' && (
                    <Badge variant="secondary" className="px-2 py-0.5">
                      Blocks runtime
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{stage.reason}</p>
              </div>
              {label && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="justify-start p-0 md:justify-end"
                  disabled={actionDisabled}
                  onClick={() => openSetupTarget(target, readiness, actions)}
                >
                  {label}
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
